import React, { useState, useEffect, useCallback } from 'react';
import config from '../config';
import { getCustomerDisplayName } from '../utils/customerUtils';

// ── Status metadata ──────────────────────────────────────────────────────────
const STATUS_META = {
  completed: { label: 'Accepted', bg: '#d1fae5', color: '#065f46', icon: 'fa-check-circle' },
  connected: { label: 'Connected', bg: '#dbeafe', color: '#1e40af', icon: 'fa-link' },
  dialing: { label: 'Dialing', bg: '#fef3c7', color: '#92400e', icon: 'fa-phone' },
  initiated: { label: 'Initiated', bg: '#fef3c7', color: '#92400e', icon: 'fa-phone' },
  rejected: { label: 'Rejected', bg: '#fee2e2', color: '#991b1b', icon: 'fa-times-circle' },
  no_answer: { label: 'Not Received', bg: '#fee2e2', color: '#991b1b', icon: 'fa-phone-slash' },
  missed: { label: 'Not Received', bg: '#fee2e2', color: '#991b1b', icon: 'fa-phone-slash' },
  failed: { label: 'Failed', bg: '#fee2e2', color: '#991b1b', icon: 'fa-exclamation-circle' },
};
const getStatusMeta = (status) =>
  STATUS_META[status] || { label: status || 'Unknown', bg: '#f3f4f6', color: '#6b7280', icon: 'fa-question-circle' };

const CALL_TYPE_META = {
  outgoing: { label: 'Outgoing', bg: '#dbeafe', color: '#1e40af', icon: 'fa-arrow-up' },
  incoming: { label: 'Incoming', bg: '#d1fae5', color: '#065f46', icon: 'fa-arrow-down' },
};
const getCallTypeMeta = (type) => CALL_TYPE_META[type] || CALL_TYPE_META.outgoing;

const CALLER_TYPE_META = {
  customer: { label: 'Customer', bg: '#eff6ff', color: '#1d4ed8' },
  driver: { label: 'Driver', bg: '#f0fdf4', color: '#15803d' },
  lead: { label: 'Lead', bg: '#fef3c7', color: '#b45309' },
  transporter: { label: 'Transporter', bg: '#faf5ff', color: '#7e22ce' },
  unknown: { label: 'Unknown', bg: '#f1f5f9', color: '#475569' },
};

const ENTITY_LABELS = {
  trip: 'Trip',
  booking: 'Booking',
  draft_booking: 'Draft Booking',
  lead: 'Lead',
  vehicle_lead: 'Vehicle Lead',
  transporter: 'Transporter',
  general_enquiry: 'General Enquiry',
};

const getLinkStatusBadge = (status) => {
  switch (status) {
    case 'auto': return { bg: '#d1fae5', color: '#065f46', border: '1px solid #a7f3d0' };
    case 'confirmed': return { bg: '#dbeafe', color: '#1e40af', border: '1px solid #bfdbfe' };
    case 'needs_review': return { bg: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' };
    case 'rejected': return { bg: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' };
    default: return { bg: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' };
  }
};

const formatDuration = (seconds) => {
  if (!seconds || seconds <= 0) return '0s';
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
};

const formatDateTime = (isoString) => {
  if (!isoString) return 'N/A';
  try {
    const d = new Date(isoString);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = d.toDateString() === yesterday.toDateString();

    const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (isToday) return `Today ${time}`;
    if (isYesterday) return `Yesterday ${time}`;
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) + ` ${time}`;
  } catch {
    return isoString;
  }
};

const formatRelativeTime = (isoString) => {
  if (!isoString) return '';
  try {
    const d = new Date(isoString);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHrs = Math.floor(diffMins / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;
    const diffDays = Math.floor(diffHrs / 24);
    return `${diffDays}d ago`;
  } catch {
    return '';
  }
};

// Markdown renderer for notes (## headings + - bullets)
const renderMarkdownNotes = (text) => {
  if (!text) return null;
  const lines = text.split('\n');
  const elements = [];
  let bulletItems = [];
  let skipUntilNextHeading = false;

  const flushBullets = () => {
    if (bulletItems.length > 0) {
      elements.push(
        <ul key={`bullets-${elements.length}`} style={{ margin: '4px 0 8px 18px', padding: 0, listStyle: 'disc' }}>
          {bulletItems.map((b, i) => (
            <li key={i} style={{ marginBottom: 3, lineHeight: 1.4 }}>{b}</li>
          ))}
        </ul>
      );
      bulletItems = [];
    }
  };

  for (const line of lines) {
    const headingMatch = line.match(/^##\s+(.+)/);
    if (headingMatch) {
      const heading = headingMatch[1].trim().toLowerCase();
      if (heading === 'summary') {
        flushBullets();
        skipUntilNextHeading = true;
        continue;
      }
      skipUntilNextHeading = false;
      flushBullets();
      elements.push(
        <div
          key={`h-${elements.length}`}
          style={{
            fontWeight: 700,
            fontSize: '0.8rem',
            color: '#1e40af',
            marginTop: elements.length > 0 ? 10 : 0,
            marginBottom: 2,
            borderBottom: '1px solid #e2e8f0',
            paddingBottom: 2,
          }}
        >
          {headingMatch[1].trim()}
        </div>
      );
      continue;
    }

    if (skipUntilNextHeading) continue;

    const bulletMatch = line.match(/^\s*[-*]\s+(.+)/);
    if (bulletMatch) {
      bulletItems.push(bulletMatch[1].trim());
      continue;
    }

    const trimmed = line.trim();
    if (trimmed) {
      flushBullets();
      elements.push(
        <div key={`p-${elements.length}`} style={{ marginTop: 2, lineHeight: 1.45 }}>
          {trimmed}
        </div>
      );
    }
  }
  flushBullets();
  return elements;
};

const collectLinkedEntities = (log) => {
  const order = ['trip', 'booking', 'draft_booking', 'lead', 'transporter'];
  const seen = new Set();
  const items = [];
  const add = (type, id) => {
    if (!type || type === 'general_enquiry' || id == null || id === '') return;
    const key = `${type}:${id}`;
    if (seen.has(key)) return;
    seen.add(key);
    items.push({ type, id });
  };

  add(log.linked_entity_type, log.linked_entity_id);
  (Array.isArray(log.link_candidates) ? log.link_candidates : []).forEach((c) => {
    if (c && (c.role === 'also_linked' || c.type)) add(c.type, c.id);
    (c && c.rows ? c.rows : []).forEach((row) => {
      if (row && row.type === log.linked_entity_type && String(row.id) === String(log.linked_entity_id) && row.booking_id) {
        add('booking', row.booking_id);
      }
    });
  });

  return items.sort((a, b) => order.indexOf(a.type) - order.indexOf(b.type));
};

const getCallerDisplayName = (log) => {
  if (log.caller_type === 'customer') {
    return (
      getCustomerDisplayName(log.customer_name, log.customer_business_name, log.customer_phone, null)
      || log.customer_name
      || 'Customer'
    );
  }
  if (log.caller_type === 'lead') {
    return log.lead_name || log.resolved_name || log.lead_phone || 'Lead';
  }
  if (log.caller_type === 'driver') {
    return log.driver_name || log.transporter_agency_name || log.resolved_name || 'Driver';
  }
  return log.resolved_name || log.driver_name || log.customer_name || log.lead_name || 'Caller';
};

const getCallerSubtext = (log) => {
  if (log.caller_type === 'customer') {
    return log.customer_business_name && log.customer_name ? log.customer_business_name : null;
  }
  if (log.caller_type === 'driver') {
    if (log.transporter_agency_name && log.transporter_agency_name !== log.driver_name) {
      return log.transporter_agency_name;
    }
    if (log.registration_no) {
      return log.registration_no;
    }
  }
  return null;
};

const getCallerPhone = (log) => {
  return log.customer_phone || log.driver_phone || log.lead_phone || log.unknown_number || null;
};

/**
 * LinkedCallLogs component
 * Renders linked call logs matching the Customer/Driver call logs list style and expanded panel layout.
 */
const LinkedCallLogs = ({ entityType, entityId, style }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [expandedLogId, setExpandedLogId] = useState(null);
  const [playingRecordingId, setPlayingRecordingId] = useState(null);
  const [fetched, setFetched] = useState(false);

  const fetchLogs = useCallback(async () => {
    if (!entityType || !entityId) return;
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token') || config.id_token || '';
      const url =
        `${config.opsApiBase}/call-logs/for-entity` +
        `?entity_type=${encodeURIComponent(entityType)}` +
        `&entity_id=${encodeURIComponent(entityId)}` +
        `&limit=50`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setLogs(data.data || []);
      setFetched(true);
    } catch (err) {
      setError(err.message || 'Failed to load call logs');
      setFetched(true);
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  // Pre-fetch call logs immediately on mount so the count is displayed
  // before the user even clicks the button (allowing them to see natural numbers > 0 vs 0).
  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleToggle = (e) => {
    e.stopPropagation();
    if (!fetched && !loading) {
      fetchLogs();
    }
    setExpanded((v) => !v);
  };

  const renderLinkDetails = (log) => {
    const hasLink = !!log.linked_entity_type;
    const linkedEntities = collectLinkedEntities(log);
    const aboutLines = (log.conversation_summary || '').split('\n').map((l) => l.trim()).filter(Boolean).slice(0, 2).join(' ');
    const aboutText = (log.call_subject || aboutLines || '').trim();

    return (
      <div style={{
        marginTop: 14, background: '#f8fafc', border: '1px solid #e2e8f0',
        borderRadius: 10, padding: '14px 16px', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.02)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 24, height: 24, borderRadius: '50%', background: '#e0e7ff', color: '#4338ca', fontSize: 11,
            }}>
              <i className="fa fa-link" />
            </span>
            <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e293b' }}>Call Link Details</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {hasLink ? (
              <>
                {linkedEntities.length > 0 ? (
                  linkedEntities.map((item) => (
                    <span key={`${item.type}-${item.id}`} style={{
                      background: '#0f172a', color: '#f8fafc', padding: '3px 10px',
                      borderRadius: 12, fontSize: 11, fontWeight: 600,
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                    }}>
                      <i className="fa fa-tag" style={{ fontSize: 9, opacity: 0.7 }} />
                      {ENTITY_LABELS[item.type] || item.type} #{item.id}
                    </span>
                  ))
                ) : (
                  <span style={{
                    background: '#0f172a', color: '#f8fafc', padding: '3px 10px',
                    borderRadius: 12, fontSize: 11, fontWeight: 600,
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                  }}>
                    <i className="fa fa-tag" style={{ fontSize: 9, opacity: 0.7 }} />
                    {ENTITY_LABELS[log.linked_entity_type] || log.linked_entity_type} #{log.linked_entity_id}
                  </span>
                )}
                {log.link_status && (
                  <span style={{
                    ...getLinkStatusBadge(log.link_status), padding: '3px 10px', borderRadius: 12,
                    fontSize: 11, fontWeight: 600, textTransform: 'capitalize',
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                  }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: getLinkStatusBadge(log.link_status).color }} />
                    {String(log.link_status).replace('_', ' ')}
                  </span>
                )}
                {log.link_confidence != null && (
                  <span style={{
                    background: '#fff', color: '#475569', border: '1px solid #e2e8f0',
                    padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 500,
                  }}>
                    confidence {Number(log.link_confidence).toFixed(2)}
                  </span>
                )}
              </>
            ) : (
              <span style={{
                background: '#fff', color: '#64748b', border: '1px solid #e2e8f0',
                padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 500,
              }}>
                Not linked yet
              </span>
            )}
          </div>
        </div>

        {hasLink && aboutText && (
          <div style={{
            background: '#ffffff', border: '1px solid #e2e8f0', borderLeft: '4px solid #3b82f6',
            borderRadius: 6, padding: '9px 13px', margin: '10px 0 12px',
            fontSize: '0.84rem', fontWeight: 600, color: '#1e293b',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <i className="fa fa-info-circle" style={{ color: '#3b82f6', fontSize: 13, flexShrink: 0 }} />
            <span>{aboutText}</span>
          </div>
        )}

        {hasLink && (
          <div style={{
            display: 'grid', gridTemplateColumns: log.linked_by ? 'minmax(180px, auto) 1fr' : '1fr',
            gap: 16, background: '#ffffff', border: '1px solid #e2e8f0',
            borderRadius: 8, padding: '12px 16px', fontSize: '0.82rem',
          }}>
            {log.linked_by && (
              <div style={{ borderRight: '1px solid #f1f5f9', paddingRight: 16 }}>
                <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 }}>
                  Linked By
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '2px 8px', borderRadius: 6, background: '#f1f5f9',
                    fontWeight: 600, color: '#334155', textTransform: 'uppercase', fontSize: 11,
                  }}>
                    <i className="fa fa-magic" style={{ fontSize: 10, color: '#6366f1' }} />
                    {log.linked_by}
                  </span>
                </div>
                {log.linked_at && (
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                    <i className="fa fa-clock-o" style={{ fontSize: 10, marginRight: 3, color: '#94a3b8' }} />
                    {formatDateTime(log.linked_at)}
                  </div>
                )}
              </div>
            )}
            <div>
              <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 }}>
                Analysis & Reasoning
              </div>
              <div style={{ lineHeight: 1.55, color: '#334155', fontSize: '0.83rem' }}>
                {log.link_reasoning || log.conversation_summary || 'No detailed reasoning recorded for this call.'}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ marginTop: 12, ...style }} onClick={(e) => e.stopPropagation()}>
      {/* Section toggle button */}
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 7,
          cursor: 'pointer',
          padding: '6px 14px',
          borderRadius: 8,
          background: expanded
            ? '#eff6ff'
            : (fetched && logs.length > 0 ? '#f0f7ff' : '#f8fafc'),
          border: `1px solid ${
            expanded
              ? '#93c5fd'
              : (fetched && logs.length > 0 ? '#bfdbfe' : '#e2e8f0')
          }`,
          userSelect: 'none',
          boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
          transition: 'all 0.15s ease',
        }}
        onClick={handleToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && handleToggle(e)}
        title={
          loading && !fetched
            ? 'Loading call logs count...'
            : fetched
            ? (logs.length > 0 ? `${logs.length} linked call log(s). Click to view.` : '0 linked call logs')
            : 'Click to load call logs'
        }
      >
        <i
          className="fa fa-phone"
          style={{
            color: (fetched && logs.length > 0) || expanded ? '#2563eb' : '#94a3b8',
            fontSize: 12,
          }}
        />
        <span
          style={{
            fontWeight: (fetched && logs.length > 0) || expanded ? 600 : 500,
            fontSize: 12,
            color: (fetched && logs.length > 0) || expanded ? '#1e40af' : '#475569',
          }}
        >
          Linked Call Logs
        </span>

        {/* Loading spinner while fetching count */}
        {loading && !fetched && (
          <i className="fa fa-spinner fa-spin" style={{ fontSize: 11, color: '#3b82f6', marginLeft: 2 }} />
        )}

        {/* Count badge pre-displayed on button: natural numbers (>0) in vibrant blue, 0 in muted grey */}
        {fetched && (
          <span
            style={{
              fontSize: 11,
              color: logs.length > 0 ? '#1e40af' : '#94a3b8',
              background: logs.length > 0 ? '#dbeafe' : '#f1f5f9',
              border: `1px solid ${logs.length > 0 ? '#bfdbfe' : '#e2e8f0'}`,
              borderRadius: 10,
              padding: '1px 7px',
              fontWeight: logs.length > 0 ? 700 : 500,
              minWidth: 18,
              textAlign: 'center',
              display: 'inline-block',
            }}
          >
            {logs.length}
          </span>
        )}

        <i
          className={`fa fa-chevron-${expanded ? 'up' : 'down'}`}
          style={{
            fontSize: 10,
            color: (fetched && logs.length > 0) || expanded ? '#64748b' : '#94a3b8',
            marginLeft: 2,
          }}
        />
      </div>

      {/* Expanded Call Logs Container */}
      {expanded && (
        <div style={{
          marginTop: 10,
          background: '#fff',
          borderRadius: 12,
          border: '1px solid #e5e7eb',
          overflow: 'hidden',
          boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}>
          {/* Header */}
          <div style={{
            padding: '10px 16px',
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            borderBottom: '2px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className="fa fa-phone" style={{ color: '#6b7280', fontSize: 12 }} />
              <span style={{ fontWeight: 600, color: '#4b5563', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                Linked Call Logs
              </span>
              {!loading && (
                <span style={{
                  background: '#e5e7eb', color: '#6b7280',
                  padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                }}>
                  {logs.length}
                </span>
              )}
            </div>
            <button
              onClick={() => fetchLogs()}
              disabled={loading}
              title="Refresh logs"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 28, height: 28, background: '#fff', border: '1px solid #e5e7eb',
                borderRadius: 6, color: '#6b7280', cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              <i className={`fa ${loading ? 'fa-spinner fa-spin' : 'fa-refresh'}`} style={{ fontSize: 11 }} />
            </button>
          </div>

          {/* Body Content */}
          {loading && (
            <div style={{ textAlign: 'center', padding: '32px 16px' }}>
              <div style={{
                width: 26, height: 26, border: '3px solid #e5e7eb',
                borderTopColor: '#3b82f6', borderRadius: '50%',
                animation: 'spin 0.8s linear infinite', margin: '0 auto 10px',
              }} />
              <p style={{ color: '#6b7280', fontSize: 12, margin: 0 }}>Loading linked call logs...</p>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          {!loading && error && (
            <div style={{ padding: '24px 16px', textAlign: 'center', color: '#dc2626', fontSize: 13 }}>
              <i className="fa fa-exclamation-circle" style={{ marginRight: 6 }} />
              {error}
            </div>
          )}

          {!loading && !error && logs.length === 0 && (
            <div style={{ textAlign: 'center', padding: '32px 16px' }}>
              <div style={{
                width: 40, height: 40, borderRadius: '50%',
                background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 10px',
              }}>
                <i className="fa fa-phone" style={{ color: '#9ca3af', fontSize: 16 }} />
              </div>
              <p style={{ color: '#6b7280', fontSize: 13, margin: '0 0 2px', fontWeight: 500 }}>
                No call logs linked
              </p>
              <p style={{ color: '#9ca3af', fontSize: 11, margin: 0 }}>
                No calls recorded for this {entityType === 'draft_booking' ? 'draft booking' : entityType.replace(/_/g, ' ')} yet.
              </p>
            </div>
          )}

          {!loading && !error && logs.length > 0 && (
            <div style={{ overflowX: 'auto', width: '100%' }}>
              {logs.map((log, idx) => {
                const statusMeta = getStatusMeta(log.status);
                const callTypeMeta = getCallTypeMeta(log.call_type);
                const callerTypeMeta = CALLER_TYPE_META[log.caller_type] || CALLER_TYPE_META.unknown;
                const isExpanded = expandedLogId === log.id;
                const callerName = getCallerDisplayName(log);
                const callerSubtext = getCallerSubtext(log);
                const callerPhone = getCallerPhone(log);

                return (
                  <div key={log.id}>
                    {/* Row */}
                    <div
                      onClick={() => setExpandedLogId(isExpanded ? null : log.id)}
                      style={{
                        padding: '14px 20px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 16,
                        cursor: 'pointer',
                        borderBottom: idx < logs.length - 1 ? '1px solid #f3f4f6' : 'none',
                        transition: 'background 0.15s ease',
                        background: isExpanded ? '#f8fafc' : '#fff',
                        minWidth: 640,
                      }}
                      onMouseEnter={(e) => { if (!isExpanded) e.currentTarget.style.background = '#fafbfc'; }}
                      onMouseLeave={(e) => { if (!isExpanded) e.currentTarget.style.background = '#fff'; }}
                    >
                      {/* Status badge */}
                      <div style={{ minWidth: 100, flexShrink: 0 }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          padding: '4px 10px', borderRadius: 6,
                          fontSize: 12, fontWeight: 600,
                          background: statusMeta.bg, color: statusMeta.color,
                        }}>
                          <i className={`fa ${statusMeta.icon}`} style={{ fontSize: 10 }} />
                          {statusMeta.label}
                        </span>
                      </div>

                      {/* Caller info */}
                      <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 600, fontSize: 13, color: '#1f2937' }}>
                            {callerName}
                          </span>
                          {callerSubtext && (
                            <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 400 }}>
                              {callerSubtext}
                            </span>
                          )}
                          <span style={{
                            fontSize: 10, fontWeight: 600, textTransform: 'capitalize',
                            padding: '1px 6px', borderRadius: 4,
                            background: callerTypeMeta.bg, color: callerTypeMeta.color,
                          }}>
                            {callerTypeMeta.label}
                          </span>
                          {log.vehicle_id && (
                            <span style={{
                              fontSize: 11, fontWeight: 600, padding: '1px 6px', borderRadius: 4,
                              background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0',
                            }}>
                              Vehicle #{log.vehicle_id}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 3, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                          {callerPhone ? (
                            <span style={{ color: '#e11d48', fontWeight: 600 }}>
                              {callerPhone}
                            </span>
                          ) : (
                            <span>N/A</span>
                          )}
                          {log.sim_number && (
                            <span style={{ color: '#6b7280' }}>
                              <i className="fa fa-phone" style={{ fontSize: 10, marginRight: 3, color: '#9ca3af' }} />
                              SIM: {log.sim_number}
                              {log.sim_role && <span style={{ marginLeft: 3, fontSize: 11, color: '#9ca3af' }}>({log.sim_role})</span>}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Call direction */}
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        <span style={{
                          padding: '3px 8px', borderRadius: 5, fontSize: 11, fontWeight: 500,
                          background: callTypeMeta.bg, color: callTypeMeta.color,
                        }}>
                          <i className={`fa ${callTypeMeta.icon}`} style={{ fontSize: 9, marginRight: 3 }} />
                          {callTypeMeta.label}
                        </span>
                      </div>

                      {/* Duration */}
                      <div style={{ minWidth: 50, textAlign: 'right', flexShrink: 0 }}>
                        <span style={{ fontSize: 13, fontWeight: 500, color: '#374151' }}>
                          {formatDuration(log.duration_seconds)}
                        </span>
                      </div>

                      {/* Time */}
                      <div style={{ minWidth: 110, textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontSize: 13, color: '#374151' }}>
                          {formatDateTime(log.started_at || log.created_at)}
                        </div>
                        <div style={{ fontSize: 11, color: '#9ca3af' }}>
                          {formatRelativeTime(log.started_at || log.created_at)}
                        </div>
                      </div>

                      {/* Recording indicator */}
                      <div style={{ minWidth: 20, textAlign: 'center', flexShrink: 0 }}>
                        {log.recording_url && (
                          <i className="fa fa-microphone" style={{ color: '#10b981', fontSize: 13 }} title="Recording available" />
                        )}
                      </div>

                      {/* Summary indicator */}
                      <div style={{ minWidth: 20, textAlign: 'center', flexShrink: 0 }}>
                        {(log.has_conversation_summary || log.conversation_summary) && (
                          <i className="fa fa-file-text-o" style={{ color: '#6366f1', fontSize: 13 }} title="Summary available" />
                        )}
                      </div>

                      {/* Expand chevron */}
                      <div style={{ minWidth: 16, textAlign: 'center', flexShrink: 0 }}>
                        <i className={`fa fa-chevron-${isExpanded ? 'up' : 'down'}`} style={{ color: '#9ca3af', fontSize: 11 }} />
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div style={{
                        padding: '0 20px 16px',
                        background: '#f8fafc',
                        borderBottom: idx < logs.length - 1 ? '1px solid #e5e7eb' : 'none',
                      }}>
                        <div style={{
                          background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb',
                          padding: 16, marginTop: 4,
                        }}>
                          {/* Details grid */}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 16 }}>
                            <div>
                              <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2, fontWeight: 500 }}>Call ID</div>
                              <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>#{log.id}</div>
                            </div>
                            <div>
                              <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2, fontWeight: 500 }}>Caller Type</div>
                              <span style={{
                                padding: '3px 8px', borderRadius: 5, fontSize: 12, fontWeight: 600,
                                background: callerTypeMeta.bg, color: callerTypeMeta.color,
                              }}>
                                {callerTypeMeta.label}
                              </span>
                            </div>
                            <div>
                              <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2, fontWeight: 500 }}>Direction</div>
                              <span style={{
                                padding: '3px 8px', borderRadius: 5, fontSize: 12, fontWeight: 600,
                                background: callTypeMeta.bg, color: callTypeMeta.color,
                              }}>
                                <i className={`fa ${callTypeMeta.icon}`} style={{ fontSize: 10, marginRight: 4 }} />
                                {callTypeMeta.label}
                              </span>
                            </div>
                            <div>
                              <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2, fontWeight: 500 }}>Operator</div>
                              <div style={{ fontSize: 13, color: '#374151' }}>
                                {log.operator_id ? `User #${log.operator_id}` : <span style={{ color: '#9ca3af' }}>System</span>}
                              </div>
                            </div>
                            <div>
                              <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2, fontWeight: 500 }}>Started</div>
                              <div style={{ fontSize: 13, color: '#374151' }}>{formatDateTime(log.started_at)}</div>
                            </div>
                            <div>
                              <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2, fontWeight: 500 }}>Ended</div>
                              <div style={{ fontSize: 13, color: '#374151' }}>{formatDateTime(log.ended_at)}</div>
                            </div>
                            <div>
                              <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2, fontWeight: 500 }}>Created</div>
                              <div style={{ fontSize: 13, color: '#374151' }}>{formatDateTime(log.created_at)}</div>
                            </div>
                            <div>
                              <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2, fontWeight: 500 }}>SIM Number</div>
                              <div style={{ fontSize: 13, color: '#374151' }}>
                                {log.sim_number || 'N/A'} {log.sim_role && <span style={{ color: '#9ca3af', fontSize: 11 }}>({log.sim_role})</span>}
                              </div>
                            </div>
                            <div>
                              <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2, fontWeight: 500 }}>Caller Phone</div>
                              <div style={{ fontSize: 13, fontWeight: 600, color: '#e11d48' }}>
                                {callerPhone || 'N/A'}
                              </div>
                            </div>
                          </div>

                          {/* Recording */}
                          {log.recording_url && (
                            <div style={{ marginBottom: 14 }}>
                              <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 6, fontWeight: 500 }}>Recording</div>
                              {playingRecordingId === log.id ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <audio controls autoPlay src={log.recording_url} style={{ height: 32, flex: 1, maxWidth: 420 }} onEnded={() => setPlayingRecordingId(null)} />
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); setPlayingRecordingId(null); }}
                                    style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 12 }}
                                  >
                                    <i className="fa fa-times" /> Close
                                  </button>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); setPlayingRecordingId(log.id); }}
                                  style={{
                                    padding: '6px 14px', background: '#eff6ff', border: '1px solid #bfdbfe',
                                    borderRadius: 6, fontSize: 12, color: '#1d4ed8', cursor: 'pointer',
                                    display: 'inline-flex', alignItems: 'center', gap: 5, fontWeight: 500,
                                  }}
                                >
                                  <i className="fa fa-play-circle" /> Play Recording
                                </button>
                              )}
                            </div>
                          )}

                          {/* Summary */}
                          {log.conversation_summary && (
                            <div style={{ marginBottom: 14 }}>
                              <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 6, fontWeight: 500 }}>Summary</div>
                              <div style={{
                                background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
                                padding: 12, fontSize: 13, lineHeight: 1.55, color: '#1e293b',
                              }}>
                                {log.conversation_summary}
                              </div>
                            </div>
                          )}

                          {/* Ops / Customer notes (from audio) */}
                          {log.conversation_transcript && log.conversation_transcript !== log.conversation_summary && (
                            <div style={{ marginBottom: 14 }}>
                              <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 6, fontWeight: 500 }}>Ops / Customer notes (from audio)</div>
                              <div style={{
                                background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
                                padding: 12, fontSize: 12, lineHeight: 1.45, color: '#334155',
                                maxHeight: 240, overflowY: 'auto',
                              }}>
                                {renderMarkdownNotes(log.conversation_transcript)}
                              </div>
                            </div>
                          )}

                          {/* Link Details */}
                          {renderLinkDetails(log)}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LinkedCallLogs;
